/**
 * Quality tiers (spec Section 9): High (glow, particles, 40-row window),
 * Medium (reduced glow, 32 rows), Low (no glow → brighter emissive, 24 rows,
 * no particles). "auto" resolves via a 2 s hidden benchmark on the title
 * screen, user-overridable in settings.
 */

export type QualityTier = "low" | "med" | "high";

export interface QualityParams {
  rowsAhead: number;
  glow: boolean;
  glowIntensity: number;
  glowKernel: number;
  particles: boolean;
  trail: boolean;
  /** Low tier compensates for missing glow with brighter emissive. */
  emissiveBoost: number;
  /** Roadside deco + tunnel shell/ribs/portals (R2-M4 §6.3/§2.3). */
  decorate: boolean;
  /** Screen-space speed-lines above SPEEDLINE_MIN_EFFRATE (VFX-1). */
  speedLines: boolean;
}

export const QUALITY: Record<QualityTier, QualityParams> = {
  high: { rowsAhead: 40, glow: true, glowIntensity: 0.7, glowKernel: 32, particles: true, trail: true, emissiveBoost: 1, decorate: true, speedLines: true },
  med: { rowsAhead: 32, glow: true, glowIntensity: 0.5, glowKernel: 16, particles: true, trail: true, emissiveBoost: 1, decorate: true, speedLines: true },
  low: { rowsAhead: 24, glow: false, glowIntensity: 0, glowKernel: 0, particles: false, trail: false, emissiveBoost: 1.35, decorate: false, speedLines: false },
};

/** Map a measured average fps to a tier. */
export function tierForFps(fps: number): QualityTier {
  if (fps >= 55) return "high";
  if (fps >= 38) return "med";
  return "low";
}

/**
 * Count rAF frames for `seconds` and resolve the tier. Runs hidden behind
 * the title screen; rendering continues during measurement.
 */
export function benchmarkTier(seconds = 2): Promise<QualityTier> {
  return new Promise((resolve) => {
    let frames = 0;
    const start = performance.now();
    const loop = (): void => {
      frames++;
      if (performance.now() - start < seconds * 1000) requestAnimationFrame(loop);
      else resolve(tierForFps(frames / seconds));
    };
    requestAnimationFrame(loop);
  });
}
